import Icon from '@ant-design/icons';
import { Button, Drawer, DrawerProps, message, Spin } from 'antd';
import { useTranslate } from '@tolgee/react';
import { ReactComponent as DeletedTodoSvg } from 'assets/icons/deleted-todo.svg';
import useModal from 'hooks/useModal';
import type { FilterValue } from './TodoFilter';
import { StatusFilter } from './TodoFilter';
import { useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { todoList } from '../service';
import TodoTable from './TodoTable';
import TodoDrawer from './TodoDrawer';
import { get, mapValues, reduce } from 'lodash-es';
import Empty from 'components/Empty';

type DeletedListProps = {
  filterValue: FilterValue;
};

const DeletedList = (props: DeletedListProps) => {
  const { t } = useTranslate();
  const { visible, show, hide } = useModal();
  return (
    <>
      {visible && <DeletedListImplement {...props} visible={visible} onClose={() => hide()} />}
      <Button onClick={() => show()} type="text" icon={<Icon component={DeletedTodoSvg} />}>
        {t('st_4')}
      </Button>
    </>
  );
};

interface DeletedListImplementProps extends DrawerProps, DeletedListProps {}
const DeletedListImplement = (props: DeletedListImplementProps) => {
  const { filterValue, ...restProps } = props;
  const [todoStatus, setTodoStatus] = useState<FilterValue['todoStatus']>(-1);
  const { t } = useTranslate();
  const [pageInfo, setPageInfo] = useState({
    pageNum: 1,
    pageSize: 10,
  });

  const params = useMemo<FilterValue>(() => {
    return {
      ...filterValue,
      type: -999,
      todoStatus,
      ...pageInfo,
    };
  }, [filterValue, todoStatus, pageInfo]);

  const [todoStatusCountMap, setTodoStatusCountMap] = useState<Record<Todo.Status, number>>();
  const { data, loading, refresh } = useRequest(() => todoList(params as any), {
    refreshDeps: [params],
    onSuccess: (data) => {
      setTodoStatusCountMap(get(data, 'todoStatusCountMap'));
    },
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });

  const { visible, show, hide, meta } = useModal<Todo.Record>();
  const drawer = meta && (
    <TodoDrawer todo={meta} visible={visible} onClose={() => hide()} onUpdate={() => refresh()} />
  );
  const noData =
    todoStatusCountMap && reduce(mapValues(todoStatusCountMap), (acc, cur) => acc + cur, 0);
  const dataSource = useMemo(() => get(data, 'todoInstances'), [data]);

  return (
    <Drawer title={t('st_4')} {...restProps} width={1200}>
      {loading && todoStatusCountMap === undefined ? (
        <div className="h-full flex items-center justify-center">
          <Spin />
        </div>
      ) : noData === 0 ? (
        <div className="h-full flex items-center justify-center">
          <Empty />
        </div>
      ) : (
        <>
          <StatusFilter
            todoStatusCountMap={todoStatusCountMap}
            filter={{ todoStatus }}
            onFilterChange={({ todoStatus }) => {
              setTodoStatus(todoStatus);
            }}
          />
          {drawer}

          <TodoTable
            showSubType
            className="!px-0 mt-[16px]"
            loading={loading}
            dataSource={dataSource}
            onCheckDetail={show}
            pagination={{
              ...pageInfo,
              total:
                todoStatus === -1
                  ? Object.values(todoStatusCountMap || {}).reduce((res, cur) => res + cur, 0)
                  : todoStatusCountMap?.[todoStatus],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (x) => t('feedback.共x项', { x }),
              onChange: (current, pageSize) => {
                setPageInfo((prevState) => {
                  return {
                    ...prevState,
                    pageNum: current,
                    pageSize,
                  };
                });
              },
            }}
            style={{ flex: '1 0 0', padding: '0 24px' }}
          />
        </>
      )}
    </Drawer>
  );
};

export default DeletedList;
